import * as React from 'react'
import { ReactElement, useCallback, useEffect, useMemo } from 'react'
import { Button, DialogActions, DialogBody, DialogTitle, DialogTrigger, makeStyles, mergeClasses, Tooltip } from '@fluentui/react-components'
import useAxios from '../../../../hooks/useAxios'
import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { HammockFormDialogProps } from '../HammockFormDialog'
import { TimeMutationDto } from '../../../../config/registrationConfig'
import { FormikConfig, useFormikContext } from 'formik'
import { bundleIcon, DismissFilled, EditOffFilled, EditOffRegular, SaveFilled, SaveRegular } from '@fluentui/react-icons'
import { useApplicationStyles } from '../../../../pages/App'
import TimeMutationDialogBodyContent from './TimeMutationDialogBodyContent'
import { useCounter } from 'usehooks-ts'
import { TimeSuggestionStatus } from '../../../../pages/Time/BaseTimeSuggestionOverview'
import { markCanceled } from '../../../../helpers/aiCancelFilter'

const CloseIcon = bundleIcon(DismissFilled, DismissFilled)
const DontTrackIcon = bundleIcon(EditOffFilled, EditOffRegular)
const TrackIcon = bundleIcon(SaveFilled, SaveRegular)

const useStyles = makeStyles({
    actions: {
        width: '100%',
    },
})

export type TrackTimeSuggestionDto = TimeMutationDto & {
    timeSuggestionId: string
}

export default function TimeSuggestionMutationDialogBody(props: HammockFormDialogProps<TrackTimeSuggestionDto>): ReactElement {
    const axios = useAxios()

    const appStyles = useApplicationStyles()
    const styles = useStyles()

    const { t: translate } = useTranslation()
    const { isViewport, viewport } = useViewport()

    const { count: focusTrigger, increment: updateFocusTrigger } = useCounter(0)
    const { values, submitForm, isSubmitting, resetForm } = useFormikContext<TrackTimeSuggestionDto>()

    const handleSubmit = useCallback<FormikConfig<TrackTimeSuggestionDto>['onSubmit']>(
        values => axios.post(`/time-suggestion/${values.timeSuggestionId}/time`, values, { headers: { SuppressErrors: 'true' } }),
        [],
    )

    useEffect(() => {
        props.setOnSubmit?.(handleSubmit)
    }, [handleSubmit])

    useEffect(() => updateFocusTrigger(), [])

    const viewportSmall = useMemo<boolean>(() => isViewport('<', Viewport.OutlookNew), [viewport])

    return (
        <DialogBody>
            <DialogTitle>{translate('time.suggestion.title')}</DialogTitle>
            <TimeMutationDialogBodyContent {...props} focusTrigger={focusTrigger} updateFocusTrigger={updateFocusTrigger} />
            <DialogActions>
                <div className={mergeClasses(styles.actions, appStyles.spaceBetween)}>
                    <DialogTrigger disableButtonEnhancement>
                        <Tooltip content={translate('time.suggestion.cancel.tooltip')} relationship={'label'} withArrow>
                            <Button
                                appearance="secondary"
                                icon={viewportSmall ? <CloseIcon /> : undefined}
                                onClick={() => {
                                    const url = `/time-suggestion/${values.timeSuggestionId}/ignore`
                                    axios
                                        .get(url, { headers: { SuppressErrors: 'true' } })
                                        .then(() => {
                                            resetForm()
                                            props.onDismiss()
                                        })
                                        .catch(error => {
                                            // The dialog unmounts on dismiss, aborting this request.
                                            // AppInsights logs that abort as a status-0 dependency
                                            // failure unless we mark it.
                                            if (error?.message === 'canceled' || error?.code === 'ERR_CANCELED') {
                                                markCanceled(url)
                                            }
                                        })
                                }}
                                disabled={isSubmitting}
                            >
                                {!viewportSmall && translate('action.cancel')}
                            </Button>
                        </Tooltip>
                    </DialogTrigger>
                    <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS)}>
                        <Button
                            appearance="primary"
                            icon={viewportSmall ? <TrackIcon /> : undefined}
                            onClick={() => submitForm()}
                            disabled={isSubmitting}
                        >
                            {!viewportSmall && translate('time.suggestion.track')}
                        </Button>
                        <Tooltip content={translate('time.suggestion.donttrack.tooltip')} relationship={'label'} withArrow>
                            <Button
                                appearance="subtle"
                                icon={viewportSmall ? <DontTrackIcon /> : undefined}
                                onClick={() => {
                                    const url = `/time-suggestion/${values.timeSuggestionId}`
                                    axios
                                        .put(
                                            url,
                                            { status: TimeSuggestionStatus.NoTracking },
                                            {
                                                headers: { SuppressErrors: 'true' },
                                            },
                                        )
                                        .then(() => props.onDismiss())
                                        .catch(error => {
                                            // The dialog unmounts on dismiss, aborting this request.
                                            // AppInsights logs that abort as a status-0 dependency
                                            // failure unless we mark it.
                                            if (error?.message === 'canceled' || error?.code === 'ERR_CANCELED') {
                                                markCanceled(url)
                                            }
                                        })
                                }}
                                disabled={isSubmitting}
                            >
                                {!viewportSmall && translate('time.suggestion.donttrack')}
                            </Button>
                        </Tooltip>
                    </div>
                </div>
            </DialogActions>
        </DialogBody>
    )
}
